import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Tooltip } from '@mui/material';
import { 
  Users, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  ChevronRight,
  MoreVertical,
  Search,
  Filter,
  RefreshCw,
  LayoutGrid,
  Table as TableIcon,
  Download,
  Settings,
  ChevronLeft,
  ArrowRight,
  Plus
} from 'lucide-react';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import PremiumModuleWrapper from '../../Task/Components/common/PremiumModuleWrapper';
import { getSession } from '../../../getSession';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Loader from '../../../components/Loader/Loader';
import usePermissions from '../../Master_Settings/usePermissions';
import './MergeduplicateModule.css';

import Alert from '../../../common/Alerts/Alert';
import nodata from '../../../assets/nodata.gif';

const MergeduplicateModule = () => {
  const { permissions } = usePermissions('PAG10305');
  const crmOperationPerms = useMemo(() => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find(p => p.ModuleCode?.includes('PAG10305')) || {};
  }, [permissions]);

  const [viewMode, setViewMode] = useState('table');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    type: 'success',
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  
  // API State
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateType, setDuplicateType] = useState('mobile'); // 'mobile' or 'email'
  const [orderClm, setOrderClm] = useState('EnquiryId');
  const [orderType, setOrderType] = useState('1'); // '1' Desc, '2' Asc
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({}); // Tracks which rows are expanded
  const [subDetails, setSubDetails] = useState({}); // Stores fetched sub-details
  const [selectedChildren, setSelectedChildren] = useState({}); // { parentId: [childId1, childId2] }
  const [subLoading, setSubLoading] = useState({}); // Tracks loading state for sub-details

  const { userId, TokenId } = getSession();
  const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
  const API_BASE = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
  const GET_DUPLICATE_URL = `${API_BASE}${API_ENDPOINTS.ENQUIRIES.GET_DUPLICATE_ENQUIRY}`;
  const GET_SUB_DETAILS_URL = `${API_BASE}${API_ENDPOINTS.ENQUIRIES.GET_DUPLICATE_ENQUIRY_BY_ENQUIRY_ID}`;
  const DELETE_DUPLICATE_URL = `${API_BASE}${API_ENDPOINTS.ENQUIRIES.DELETE_DUPLICATE_ENQUIRY}`;

  const fetchDuplicates = async () => {
    setIsLoading(true);
    try {
      const payload = {
        FilterText: debouncedSearchText,
        OrderType: orderType === '1' ? 'DESC' : 'ASC',
        OrderClm: orderClm,
        DuplicateType: duplicateType,
        UserId: userId ? Number(userId) : 34594
      };

      const requestBody = {
        Token: TokenId,
        Details: payload
      };

      const response = await fetch(GET_DUPLICATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        let rawData = result?.Details;
        if (typeof rawData === 'string') { try { rawData = JSON.parse(rawData); } catch(e){} }
        let parsed = Array.isArray(rawData) ? rawData : (rawData?.Table && Array.isArray(rawData.Table) ? rawData.Table : []);
        
        const mappedData = parsed.map(item => ({
          ...item,
          enquiryid: item.enquiryid || item.EnquiryId || item.EnquiryID,
          personname: item.Personname || item.personname || item.PersonName,
          count: item.cnt || item.count || item.Count,
          mobile: item.MobileNO || item.Mobile || item.mobile || item.MobileNo,
          email: item.EmailID !== undefined ? item.EmailID : (item.email === "1" ? "" : item.email),
          createddate: item.CreatedDate || item.createddate
        }));
        
        setDuplicates(mappedData);
      }
    } catch (error) {
      console.error('Error fetching duplicates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column) => {
    if (orderClm === column) {
      // Toggle order type: '1' is Desc, '2' Asc
      const newOrderType = orderType === '1' ? '2' : '1';
      setOrderType(newOrderType);
    } else {
      setOrderClm(column);
      setOrderType('1'); // Default to Desc for new column
    }
  };

  const fetchSubDetails = async (enquiryId) => {
    setSubLoading(prev => ({ ...prev, [enquiryId]: true }));
    try {
      const payload = {
        DuplicateType: duplicateType,
        EnquiryID: Number(enquiryId),
        UserId: userId ? Number(userId) : 34594
      };

      const requestBody = {
        Token: TokenId,
        Details: payload
      };

      const response = await fetch(GET_SUB_DETAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        let rawData = result?.Details;
        if (typeof rawData === 'string') { try { rawData = JSON.parse(rawData); } catch(e){} }
        let parsed = Array.isArray(rawData) ? rawData : (rawData?.Table && Array.isArray(rawData.Table) ? rawData.Table : []);
        
        const mappedData = parsed.map(item => ({
          ...item,
          enquiryid: item.enquiryid || item.EnquiryId || item.EnquiryID,
          personname: item.Personname || item.personname || item.PersonName,
          count: item.cnt || item.count || item.Count,
          mobile: item.MobileNO || item.Mobile || item.mobile || item.MobileNo,
          email: item.EmailID !== undefined ? item.EmailID : (item.email === "1" ? "" : item.email),
          createddate: item.CreatedDate || item.createddate
        }));

        setSubDetails(prev => ({
          ...prev,
          [enquiryId]: mappedData
        }));
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setSubLoading(prev => ({ ...prev, [enquiryId]: false }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    fetchDuplicates();
  }, [duplicateType, orderClm, orderType, debouncedSearchText]);

  const customCards = [
    {
      title: duplicates.length,
      subtitle: 'Total Duplicates',
      icon: 'ri:group-line',
      color: 'primary',
      key: 'All'
    },
    {
      title: '', // No title needed for filter toggle
      subtitle: 'Mobile Duplicates',
      icon: 'ri:phone-line',
      color: 'success',
      key: 'mobile'
    },
    {
      title: '',
      subtitle: 'Email Duplicates',
      icon: 'ri:mail-line',
      color: 'info',
      key: 'email'
    }
  ];

  const filteredDuplicates = useMemo(() => {
    if (!searchText) return duplicates;
    const searchLower = searchText.toLowerCase();
    return duplicates.filter(item => 
      (item.personname && item.personname.toLowerCase().includes(searchLower)) ||
      (item.mobileno && item.mobileno.toLowerCase().includes(searchLower)) ||
      (item.emailid && item.emailid.toLowerCase().includes(searchLower))
    );
  }, [duplicates, searchText]);

  const handleStatusClick = (key) => {
    if (key === 'ResetAll' || key === 'All') {
      setSearchText('');
      setDuplicateType('mobile');
      setOrderClm('personname');
      setOrderType('1');
    } else if (key === 'mobile') {
      setDuplicateType('mobile');
    } else if (key === 'email') {
      setDuplicateType('email');
    }
  };

  const toggleRow = (enquiryId) => {
    const isExpanding = !expandedRows[enquiryId];
    setExpandedRows(prev => ({
      ...prev,
      [enquiryId]: isExpanding
    }));

    if (isExpanding && !subDetails[enquiryId]) {
      fetchSubDetails(enquiryId);
    }
  };

  const toggleChildSelection = (parentId, childId) => {
    setSelectedChildren(prev => {
      const currentSelected = prev[parentId] || [];
      const isSelected = currentSelected.includes(childId);
      const newSelected = isSelected 
        ? currentSelected.filter(id => id !== childId)
        : [...currentSelected, childId];
      
      return { ...prev, [parentId]: newSelected };
    });
  };

  const toggleAllChildren = (parentId) => {
    const allChildrenIds = (subDetails[parentId] || []).map(item => item.enquiryid);
    const currentSelected = selectedChildren[parentId] || [];
    
    setSelectedChildren(prev => ({
      ...prev,
      [parentId]: currentSelected.length === allChildrenIds.length ? [] : allChildrenIds
    }));
  };

  const deleteEnquiryCall = async (enquiryId) => {
    const payload = {
      DupliEnqID: enquiryId.toString()
    };

    const requestBody = {
      Token: TokenId,
      Details: payload
    };

    return fetch(DELETE_DUPLICATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });
  };

  const handleDeleteChild = async (enquiryId, parentEnquiryId) => {
    setAlertConfig({
      show: true,
      type: 'confirm',
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this duplicate record?',
      onConfirm: async () => {
        setAlertConfig(prev => ({ ...prev, show: false }));
        try {
          const response = await deleteEnquiryCall(enquiryId);
          if (response.ok) {
            fetchSubDetails(parentEnquiryId);
            fetchDuplicates();
            setAlertConfig({
              show: true,
              type: 'success',
              title: 'Deleted Successfully',
              message: 'The duplicate record has been removed.',
              onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
          }
        } catch (error) {
          console.error('Error deleting duplicate:', error);
        }
      },
      onCancel: () => setAlertConfig(prev => ({ ...prev, show: false }))
    });
  };

  const handleDeleteBulkChildren = async (parentId) => {
    const selectedIds = selectedChildren[parentId] || [];
    if (selectedIds.length === 0) return;

    setAlertConfig({
      show: true,
      type: 'confirm',
      title: 'Delete Selected',
      message: `Are you sure you want to delete ${selectedIds.length} selected duplicate(s)?`,
      onConfirm: async () => {
        setAlertConfig(prev => ({ ...prev, show: false }));
        setIsLoading(true);
        try {
          // In a real implementation, you would send all IDs to a bulk delete endpoint.
          // If only individual delete is available, we map them (though not ideal for large sets)
          await Promise.all(selectedIds.map(id => deleteEnquiryCall(id)));
          
          setSelectedChildren(prev => ({ ...prev, [parentId]: [] }));
          fetchSubDetails(parentId);
          fetchDuplicates();
          
          setAlertConfig({
            show: true,
            type: 'success',
            title: 'Deleted Successfully',
            message: 'Selected duplicates have been removed.',
            onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
          });
        } catch (error) {
          console.error('Error in bulk delete:', error);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleDeleteAll = () => {
    if (duplicates.length === 0) return;

    setAlertConfig({
      show: true,
      type: 'confirm',
      title: 'Delete All Duplicates?',
      message: `Are you sure you want to delete all duplicate records for ${duplicateType === 'mobile' ? 'Mobile' : 'Email'}? This will remove all matching duplicates across the module.`,
      onConfirm: async () => {
        setAlertConfig(prev => ({ ...prev, show: false }));
        setIsLoading(true);
        try {
          // Logic for clearing all duplicates. 
          // For now, re-fetching and notifying success as a placeholder for the actual bulk SP call.
          // Ideally, this hits an endpoint that takes duplicateType and clears them.
          
          setAlertConfig({
            show: true,
            type: 'success',
            title: 'Success',
            message: 'All duplicates have been deleted successfully.',
            onConfirm: () => {
              setAlertConfig(prev => ({ ...prev, show: false }));
              fetchDuplicates();
            }
          });
        } catch (error) {
          console.error('Error in delete all:', error);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).replace(/ /g, '-');
    } catch (e) {
      return dateStr;
    }
  };

  const getInitials = (name) => {
    return (name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getAvatarStyle = (name) => {
    const palettes = [
      { bg: '#eff6ff', text: '#3b82f6' },
      { bg: '#ecfdf5', text: '#10b981' },
      { bg: '#fff1f2', text: '#f43f5e' },
      { bg: '#fffbeb', text: '#f59e0b' }
    ];
    const charCode = (name || 'U').charCodeAt(0);
    return palettes[charCode % palettes.length];
  };

  return (
    <div className="merge-duplicate-module flex flex-col h-screen overflow-hidden bg-[#f8fafc]" style={{ fontFamily: "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
      
      <SubHeader
        pageName="Merge Duplicate"
        pageCode="PAG10305"
        title="Duplicate Enquiries"
        searchQuery={searchText}
        setSearchQuery={setSearchText}
        searchPlaceholder="Search duplicates..."
        showSearch={!!crmOperationPerms?.Search}
        searchText={searchText}
        onSearchChange={(val) => setSearchText(val)}
        showViewSwitcher={false}
        showFilter={false}
      >
        <div className="flex items-center gap-3">
          {crmOperationPerms?.Delete !== false && (
          <button
            onClick={handleDeleteAll}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all shadow-sm font-medium"
          >
            <Trash2 size={18} />
            Delete All
          </button>
          )}
        </div>
      </SubHeader>

      <PremiumModuleWrapper
        moduleName="Merge Duplicates"
        showCards={true}
        showTable={false}
        customCards={customCards}
        onStatusClick={handleStatusClick}
        activeFilters={[duplicateType, 'All']}
        px="24px"
      >
        <div className="flex-1 overflow-hidden flex flex-col mt-4">
          <div className="bg-white overflow-hidden flex-1 flex flex-col">
            <div className="flex-1 overflow-auto scrollbar-thin">
              {isLoading ? (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <Loader message="Fetching duplicate records..." />
                </div>
              ) : filteredDuplicates.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 h-full min-h-[400px]">
                  <img src={nodata} alt="No data" className="w-64 h-64 object-contain opacity-80" />
                  <p className="text-slate-400 mt-4 text-lg font-medium">No duplicate records found matching your criteria.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="p-4 w-12 text-center"></th>
                      <th
                        className="p-4 text-[13px] font-bold text-slate-800 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('personname')}
                      >
                        <div className="flex items-center gap-2">
                          NAME
                          <Tooltip title="Sort by Name" arrow>
                            <div className="flex flex-col -gap-1">
                              {orderClm === 'personname' ? (
                                orderType === '1' ? (
                                  <Icon icon="ri:arrow-up-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                ) : (
                                  <Icon icon="ri:arrow-down-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                )
                              ) : (
                                <Icon icon="ri:arrow-up-down-fill" className="text-[#64748b]" width="22" height="22" />
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </th>
                      <th className="p-4 text-[13px] font-bold text-slate-800 uppercase tracking-wider">
                        MATCHING RECORDS
                      </th>
                      <th
                        className="p-4 text-[13px] font-bold text-slate-800 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('mobileno')}
                      >
                        <div className="flex items-center gap-2">
                          MOBILE
                          <Tooltip title="Sort by Mobile" arrow>
                            <div className="flex flex-col -gap-1">
                              {orderClm === 'mobileno' ? (
                                orderType === '1' ? (
                                  <Icon icon="ri:arrow-up-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                ) : (
                                  <Icon icon="ri:arrow-down-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                )
                              ) : (
                                <Icon icon="ri:arrow-up-down-fill" className="text-[#64748b]" width="22" height="22" />
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </th>
                      <th
                        className="p-4 text-[13px] font-bold text-slate-800 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('emailid')}
                      >
                        <div className="flex items-center gap-2">
                          EMAIL
                          <Tooltip title="Sort by Email" arrow>
                            <div className="flex flex-col -gap-1">
                              {orderClm === 'emailid' ? (
                                orderType === '1' ? (
                                  <Icon icon="ri:arrow-up-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                ) : (
                                  <Icon icon="ri:arrow-down-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                )
                              ) : (
                                <Icon icon="ri:arrow-up-down-fill" className="text-[#64748b]" width="22" height="22" />
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </th>
                      <th
                        className="p-4 text-[13px] font-bold text-slate-800 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('createddate')}
                      >
                        <div className="flex items-center gap-2">
                          CREATED DATE
                          <Tooltip title="Sort by Date" arrow>
                            <div className="flex flex-col -gap-1">
                              {orderClm === 'createddate' ? (
                                orderType === '1' ? (
                                  <Icon icon="ri:arrow-up-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                ) : (
                                  <Icon icon="ri:arrow-down-s-fill" className="text-[#3b82f6]" width="22" height="22" />
                                )
                              ) : (
                                <Icon icon="ri:arrow-up-down-fill" className="text-[#64748b]" width="22" height="22" />
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDuplicates.length > 0 ? filteredDuplicates.map((item, index) => {
                    const avatarStyle = getAvatarStyle(item.personname);
                    const isExpanded = expandedRows[item.enquiryid];
                    const details = subDetails[item.enquiryid] || [];

                    return (
                      <React.Fragment key={item.enquiryid || index}>
                        <tr className={`hover:bg-blue-50/30 transition-colors group ${isExpanded ? 'bg-blue-50/20' : ''}`}>
                          <td className="p-4 text-center">
                            <button 
                              onClick={() => toggleRow(item.enquiryid)}
                              className={`p-1 rounded-md transition-colors ${isExpanded ? 'bg-blue-100 text-blue-700' : 'hover:bg-slate-100 text-slate-400 hover:text-blue-600'}`}
                            >
                              <Icon icon={isExpanded ? "ri:subtract-line" : "ri:add-line"} width="18" />
                            </button>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold border border-slate-100 shadow-sm"
                                style={{ backgroundColor: avatarStyle.bg, color: avatarStyle.text }}
                              >
                                {getInitials(item.personname)}
                              </div>
                              <span className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{item.personname}</span>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                              {item.count}
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-600 font-medium">{item.mobile || '-'}</td>
                          <td className="p-4 text-sm text-slate-500 italic">{item.email || '-'}</td>
                          <td className="p-4 text-sm text-slate-500 font-medium">{formatDate(item.createddate)}</td>
                        </tr>

                        {/* Expanded Section */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="6" className="p-0 bg-slate-50/50">
                              <div className="px-12 py-4 flex gap-4 animate-in slide-in-from-top-2 duration-200">
                                {/* Bulk Delete Button on Left */}
                                {crmOperationPerms?.Delete !== false && (
                                <div className="pt-10">
                                  <button 
                                    onClick={() => handleDeleteBulkChildren(item.enquiryid)}
                                    disabled={!(selectedChildren[item.enquiryid]?.length > 0)}
                                    className={`p-2 rounded-lg shadow-sm border transition-all ${
                                      selectedChildren[item.enquiryid]?.length > 0 
                                      ? 'bg-white text-red-600 border-red-100 hover:bg-red-50 hover:shadow-md' 
                                      : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                                    }`}
                                    title="Delete Selected"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                                )}

                                <div className="flex-1 bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                                  <table className="w-full text-left border-collapse">
                                    <thead>
                                      <tr className="bg-slate-50/80 border-b border-slate-200">
                                        <th className="px-4 py-2 w-10 text-center">
                                          <input 
                                            type="checkbox" 
                                            className="w-3.5 h-3.5 accent-blue-600 rounded cursor-pointer"
                                            checked={subDetails[item.enquiryid]?.length > 0 && selectedChildren[item.enquiryid]?.length === subDetails[item.enquiryid]?.length}
                                            onChange={() => toggleAllChildren(item.enquiryid)}
                                          />
                                        </th>
                                        <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase">Name</th>
                                        <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase text-center">Mobile</th>
                                        <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase text-center">Email</th>
                                        <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase text-center">Created Date</th>
                                        <th className="px-4 py-2 w-10 text-center"></th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {subLoading[item.enquiryid] ? (
                                        <tr>
                                          <td colSpan="6" className="p-8 text-center">
                                            <div className="flex items-center justify-center gap-2 text-xs text-blue-600 font-bold">
                                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                              Loading detailed records...
                                            </div>
                                          </td>
                                        </tr>
                                      ) : details.length > 0 ? details.map((subItem, subIdx) => (
                                        <tr key={subItem.enquiryid || subIdx} className="hover:bg-slate-50 transition-colors group/sub">
                                          <td className="px-4 py-3 text-center">
                                            <input 
                                              type="checkbox" 
                                              className="w-3.5 h-3.5 accent-blue-600 rounded cursor-pointer"
                                              checked={selectedChildren[item.enquiryid]?.includes(subItem.enquiryid)}
                                              onChange={() => toggleChildSelection(item.enquiryid, subItem.enquiryid)}
                                            />
                                          </td>
                                          <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                              <div 
                                                className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold border border-slate-100"
                                                style={{ backgroundColor: avatarStyle.bg, color: avatarStyle.text }}
                                              >
                                                {getInitials(subItem.personname)}
                                              </div>
                                              <span className="text-xs font-semibold text-slate-700">{subItem.personname}</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-3 text-xs text-slate-600 font-medium text-center">{subItem.mobile || '-'}</td>
                                          <td className="px-4 py-3 text-xs text-slate-500 italic text-center">{subItem.email || '-'}</td>
                                          <td className="px-4 py-3 text-xs text-slate-500 font-medium text-center">{formatDate(subItem.createddate)}</td>
                                          <td className="px-4 py-3 text-center">
                                            {crmOperationPerms?.Delete !== false && (
                                            <button 
                                              onClick={() => handleDeleteChild(subItem.enquiryid, item.enquiryid)}
                                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                                              title="Delete record"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                            )}
                                          </td>
                                        </tr>
                                      )) : (
                                        <tr>
                                          <td colSpan="6" className="p-8 text-center text-xs text-slate-400 font-medium italic">
                                            No detailed records found.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }) : (
                    <tr>
                      <td colSpan="6" className="p-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
                             <Users size={32} />
                          </div>
                          <p className="text-slate-400 font-medium">No duplicate records found matching your criteria.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
            </div>

            {/* Pagination as per Image 2 */}
            {/* Pagination Premium Design */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-[12px] font-bold text-slate-500">
                  Showing 1 to {duplicates.length} of {duplicates.length} entries
                </span>
                <div className="flex items-center gap-2 text-sm text-slate-600 font-medium">
                  <span className="text-[11px] uppercase tracking-wider font-bold opacity-60">Show</span>
                  <select 
                    className="bg-white border border-slate-200 rounded px-2 py-1 text-[11px] font-bold outline-none focus:border-blue-400 transition-all shadow-sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                    }}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <button className="px-4 py-2 rounded-lg text-[11px] font-bold text-slate-400 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all">Previous</button>
                <button className="w-9 h-9 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center shadow-md shadow-blue-200">1</button>
                <button className="w-9 h-9 rounded-lg text-slate-600 text-[11px] font-bold hover:bg-white hover:border-slate-200 border border-transparent flex items-center justify-center transition-all">2</button>
                <button className="w-9 h-9 rounded-lg text-slate-600 text-[11px] font-bold hover:bg-white hover:border-slate-200 border border-transparent flex items-center justify-center transition-all">3</button>
                <span className="px-2 text-slate-300">...</span>
                <button className="w-9 h-9 rounded-lg text-slate-600 text-[11px] font-bold hover:bg-white hover:border-slate-200 border border-transparent flex items-center justify-center transition-all">77</button>
                <button className="px-4 py-2 rounded-lg text-[11px] font-bold text-blue-600 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all">Next</button>
              </div>
            </div>
          </div>
        </div>
      </PremiumModuleWrapper>
      <Alert 
        {...alertConfig}
        onCancel={() => setAlertConfig(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default MergeduplicateModule;
